class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () => this.querySelectorAll(this.dataset.toggleTabindex);

    this.giftWrapHandle = window.GIFT_WRAP_HANDLE;
    this.giftWrapVariantId = window.GIFT_WRAP_VARIANT_ID;
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener("cart-drawer:refresh", (e) => this.refreshCartDrawer(e));
      this.initializeGiftWrapCheckbox();
      return;
    }

    this.init();
    this.initializeGiftWrapCheckbox();

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  async getCart() {
    const res = await fetch("/cart.js");
    if (!res.ok) throw new Error("Failed to fetch cart");
    return res.json();
  }

  initializeGiftWrapCheckbox() {
    requestAnimationFrame(async () => {
      const checkbox = document.getElementById("cart_additional_checkbox");
      if (!checkbox) return;
      
      checkbox.checked = false;

      await this.syncGiftWrapCheckbox(checkbox);

      if (checkbox.dataset.bound === "true") return;
      checkbox.dataset.bound = "true";

      checkbox.addEventListener("change", async (e) => {
        try {
          if (e.target.checked) {
            await this.addGiftWrap();
          } else {
            await this.removeGiftWrap();
          }

          document.dispatchEvent(new CustomEvent("cart-drawer:refresh"));

          if (window.location.pathname === "/cart") {
            window.location.reload();
          }
        } catch (err) {
          console.error("Gift wrap update failed:", err);
        }
      });
    });
  }

  async syncGiftWrapCheckbox(checkbox) {
    try {
      const cart = await this.getCart();
      const hasGiftWrap = cart.items.some((i) => i.handle === this.giftWrapHandle);
      checkbox.checked = hasGiftWrap;
    } catch (err) {
      console.warn("Could not sync gift-wrap checkbox", err);
    }
  }

  async addGiftWrap() {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: this.giftWrapVariantId, quantity: 1 }),
    });
    if (!res.ok) throw new Error("Failed to add gift wrap");
  }

  async removeGiftWrap() {
    const cart = await this.getCart();
    const item = cart.items.find((i) => i.handle === this.giftWrapHandle);
    if (!item) return;

    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: item.key, quantity: 0 }),
    });
    if (!res.ok) throw new Error("Failed to remove gift wrap");
  }

  getFocusableElements() {
    const focusableElementsSelector = "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed = e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) this.toggleDrawerClasses();
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    document.addEventListener("cart-drawer:refresh", (e) => this.refreshCartDrawer(e));
  }

  renderContents(parsedState, isClosedCart = true) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = document.querySelector(section.selector);
      if (!sectionElement) return;

      const newInner = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        ".wt-cart__drawer__inner"
      );

      sectionElement.innerHTML = newInner;
    });

    this.initializeGiftWrapCheckbox();

    const drawerItemsEl = document.querySelector("cart-drawer-items");
    if (drawerItemsEl) window.cartItems = drawerItemsEl;

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) this.closeButton()?.focus();
      });
    }
  }


  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer .wt-cart__drawer__inner",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer() {
    const sectionsToRender = this.getSectionsToRender();
    fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`,
    )
      .then((response) => response.json())
      .then((response) => {
        const parsedState = { sections: response };
        this.renderContents(parsedState, false);
      })
      .catch((e) => console.error(e));
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: "CartDrawer", section: "cart-drawer", selector: ".drawer__inner" },
      { id: "cart-icon-bubble", section: "cart-icon-bubble", selector: ".shopify-section" },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
